import useCheckoutConfirmation from "@app-contexts/public/shared/checkout/hooks/useCheckoutConfirmation";
import CheckoutTwoPanelLayout from "@app-contexts/public/shared/checkout/templates/CheckoutTwoPanelLayout/CheckoutTwoPanelLayout.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import ConfirmationSummary from "../ConfirmationSummary/ConfirmationSummary.web";
import Contacts from "../Contacts/Contacts.web";
import DownloadApp from "../DownloadApp/DownloadApp.web";
import InvoiceFailed from "../InvoiceFailed/InvoiceFailed.web";
import OrderStatus from "../OrderStatus/OrderStatus.web";
import GroupSalesOrderStatus from "../GroupSalesOrderStatus/GroupSalesOrderStatus.web";
import PostPurchaseMessages from "../PostPurchaseMessages/PostPurchaseMessages.web";
import type { ConfirmationSharedProps } from "./Confirmation.props";
import { PostPurchaseMarketing } from "../PostPurchaseMarketing/PostPurchaseMarketing";

export default function Confirmation(): JSX.Element {
  const {
    isLoggedIn,
    postPurchaseMessages,
    basket,
    invoice,
    hasNfcEvent,
    hasVirtualEvent,
    isConfirmationEmailEnabled,
    isLoading,
    venue,
    hasProtection,
  } = useCheckoutConfirmation();

  // if the invoice is not found AND it is not a waitlist basket, notify the user.
  // waitlist baskets do not generate an invoice on payment info capture.
  if (basket && !invoice && !basket.is_waitlist) {
    return (
      <CheckoutTwoPanelLayout
        wrapperProps={{
          "data-testid": "checkout-confirmation",
        }}
        left={
          <Flex
            flexGrow={1}
            direction="column"
            gap={{ base: "xl", sm: "2xl" }}
            padding={{ base: "lg", md: "24px 24px 24px 40px" }}
          >
            <Box pt={{ base: "none", sm: "36px" }}>
              <InvoiceFailed basket={basket} />
            </Box>

            <Flex
              display={{ base: "flex", sm: "none" }}
              gap="2xl"
              direction="column"
            >
              <Box p={{ base: "none", sm: "0 24px" }}>
                <Contacts widthDivider />
              </Box>
              <Box p={{ base: "16px 0", sm: "16px 24px" }}>
                <DownloadApp />
              </Box>
            </Flex>
          </Flex>
        }
        right={<ConfirmationSummary />}
        leftColumnProps={{
          height: "auto",
          overflowY: "auto",
        }}
        rightColumnProps={{
          display: { base: "none", sm: "block" },
        }}
      />
    );
  }

  // otherwise show all information
  return (
    <>
      <PostPurchaseMarketing
        transactionId={invoice?.transaction_id}
        email={basket?.email}
        firstName={basket?.name_on_ticket?.split(" ")[0]}
        lastName={basket?.name_on_ticket?.split(" ")[1]}
        mobile={basket?.phone_number}
        country={basket?.billing_address?.country || "CA"}
        billingZipCode={basket?.billing_address?.postal || ""}
        language="en"
        eventVenue={`${basket?.payment_venue}-${basket?.purchase_source_platform}`}
        purchaseSourcePlatform={basket?.purchase_source_platform}
        venue={venue}
      />
      <CheckoutTwoPanelLayout
        wrapperProps={{
          "data-testid": "checkout-confirmation",
        }}
        left={
          <Flex
            flexGrow={1}
            direction="column"
            gap={{ base: "xl", sm: "2xl" }}
            padding={{ base: "lg", md: "24px 24px 24px 40px" }}
          >
            <Box pt={{ base: "none", sm: "36px" }}>
              {basket?.is_group_sale ? (
                <GroupSalesOrderStatus
                  transactionId={invoice?.transaction_id}
                  basket={basket}
                  isLoading={isLoading}
                />
              ) : (
                <OrderStatus
                  transactionId={invoice?.transaction_id}
                  isConfirmationEmailEnabled={isConfirmationEmailEnabled}
                  hasNfcEvent={hasNfcEvent}
                  isLoggedIn={isLoggedIn}
                  isVirtualEvent={hasVirtualEvent}
                  isLoading={isLoading}
                  basket={basket}
                  protection={hasProtection ? invoice?.protection : undefined}
                />
              )}
            </Box>

            {isLoading ? (
              <Skeleton h="element.3xl" w="100%" />
            ) : (
              <PostPurchaseMessages messages={postPurchaseMessages} />
            )}

            <Flex
              display={{ base: "flex", sm: "none" }}
              gap="2xl"
              direction="column"
            >
              <Box p={{ base: "none", sm: "0 24px" }}>
                <Contacts widthDivider />
              </Box>
              <Box p={{ base: "16px 0", sm: "16px 24px" }}>
                <DownloadApp />
              </Box>
            </Flex>
          </Flex>
        }
        right={<ConfirmationSummary />}
        leftColumnProps={{
          height: "auto",
          overflowY: "auto",
        }}
        rightColumnProps={{
          display: { base: "none", sm: "block" },
        }}
      />
    </>
  );
}

export type ConfirmationProps = ConfirmationSharedProps & {
  // Add any web specific props here.
};
